import { useEffect, useState } from "react";
import {
  IconPencil,
  IconCheck,
  IconShieldLock,
  IconBuilding,
  IconCrown,
  IconBriefcase,
  IconMapPin,
} from "@tabler/icons-react";
import { ROLE_OPTIONS } from "./data";
import styles from "../../../../pages/ClientDashboard/Profile/profile.module.css";
import Input, { Select } from "../../FormInput/input";
import Button from "../../Button/button";
import { updateClientProfile, clientTeamGetUsers, clientTeamGetTeams, clientTeamEditUser, clientTeamCreateTeam, clientTeamEditTeam } from "../../../../services/clientCalls";
import { errorHandler } from "../../../../utils";
import { getTeamApiError } from "../../../TeamManagement/utils/apiError";

function asId(value) {
  if (!value) return null;
  if (typeof value === "object") return value._id || value.id || null;
  return value;
}

function populatedName(value) {
  return value && typeof value === "object" ? value.name : null;
}

export default function RoleAccessCard({ data, onShowToast, getCompanyProfile }) {
  const [editing, setEditing] = useState(false);
  const [load, setLoad] = useState(false);
  const [memberId, setMemberId] = useState(null);
  const [teamId, setTeamId] = useState(null);
  const [savedTeam, setSavedTeam] = useState("No team yet");
  const [savedRole, setSavedRole] = useState("Admin");
  const [form, setForm] = useState({
    team: "No team yet",
    role: "Admin",
    position: "",
    department: "",
  });

  useEffect(() => {
    const loadAccess = async () => {
      const email = (localStorage.getItem("email") || "").toLowerCase();
      const storedRole = localStorage.getItem("role") || "";
      const fallbackRole =
        storedRole === "client" ? "Admin" : storedRole || "Admin";
      try {
        const [usersRes, teamsRes] = await Promise.all([
          clientTeamGetUsers({ page: 1, limit: 100 }),
          clientTeamGetTeams(),
        ]);
        const users =
          usersRes?.data?.data?.users ||
          usersRes?.data?.data?.records ||
          usersRes?.data?.data ||
          [];
        const teams =
          teamsRes?.data?.data?.teams ||
          teamsRes?.data?.data?.records ||
          teamsRes?.data?.data ||
          [];
        const me = Array.isArray(users)
          ? users.find((u) => u?.email?.toLowerCase() === email)
          : null;
        const resolvedTeamId = asId(me?.teamId) || asId(me?.team);
        const team = Array.isArray(teams)
          ? teams.find((t) => String(t?._id) === String(resolvedTeamId))
          : null;
        const nextTeam =
          team?.name ||
          populatedName(me?.teamId) ||
          populatedName(me?.team) ||
          "No team yet";
        const nextRole = me?.role || fallbackRole;
        setMemberId(asId(me?._id || me?.id));
        setTeamId(resolvedTeamId || null);
        setSavedTeam(nextTeam);
        setSavedRole(nextRole);
        setForm({
          team: nextTeam,
          role: nextRole,
          position: data?.position || "",
          department: data?.department || "",
        });
      } catch (_) {
        setForm({
          team: "No team yet",
          role: fallbackRole,
          position: data?.position || "",
          department: data?.department || "",
        });
      }
    };
    loadAccess();
  }, [data]);

  function startEdit() {
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
  }

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setLoad(true);
      const res = await updateClientProfile({
        position: form.position,
        department: form.department,
      });
      const resolvedTeamId = asId(teamId);
      const resolvedMemberId = asId(memberId);
      const teamName = (form.team || "").trim();
      const hasTeamName = Boolean(teamName) && teamName !== "No team yet";
      const teamChanged = hasTeamName && teamName !== savedTeam;
      const roleChanged = Boolean(form.role) && form.role !== savedRole;

      if (teamChanged) {
        try {
          if (resolvedTeamId) {
            await clientTeamEditTeam(resolvedTeamId, { name: teamName });
          } else {
            const created = await clientTeamCreateTeam({ name: teamName });
            const createdId = asId(
              created?.data?.data?._id ||
                created?.data?.data?.team ||
                created?.data?.data,
            );
            if (createdId && resolvedMemberId) {
              await clientTeamEditUser(resolvedMemberId, { teamId: createdId });
              setTeamId(createdId);
            }
          }
          setSavedTeam(teamName);
        } catch (teamErr) {
          errorHandler(getTeamApiError(teamErr), "Error");
        }
      }

      if (resolvedMemberId && roleChanged) {
        try {
          await clientTeamEditUser(resolvedMemberId, { role: form.role });
          setSavedRole(form.role);
        } catch (roleErr) {
          errorHandler(getTeamApiError(roleErr), "Error");
        }
      }

      if (res?.data?.success) {
        onShowToast("Access details saved successfully");
        getCompanyProfile();
      }
    } catch (error) {
      errorHandler(getTeamApiError(error), "Error");
    } finally {
      setLoad(false);
      setEditing(false);
    }
  };

  const setField = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <div className={styles.cardHeaderLeft}>
          <div className={styles.cardIcon}>
            <IconShieldLock size={18} />
          </div>
          <div>
            <div className={styles.cardTitle}>Role & Access Level</div>
            <div className={styles.cardSubtitle}>
              Your team assignment and job position
            </div>
          </div>
        </div>
        <div className={styles.btnGroup}>
          {!editing && (
            <Button
              variant="primary"
              size="sm"
              leftIcon={<IconPencil size={14} />}
              onClick={startEdit}
            >
              Edit
            </Button>
          )}
        </div>
      </div>

      <div className={styles.cardBody}>
        {!editing ? (
          <div className={styles.accessList}>
            <div className={styles.accessItem}>
              <div className={styles.accessIcon}>
                <IconBuilding size={20} />
              </div>
              <div>
                <div className={styles.accessLabel}>Team</div>
                <div className={styles.accessValue}>
                  {form.team || "No team yet"}
                </div>
              </div>
            </div>
            <div className={styles.accessItem}>
              <div
                className={styles.accessIcon}
                style={{ background: "#fef3c7", color: "#d97706" }}
              >
                <IconCrown size={20} />
              </div>
              <div>
                <div className={styles.accessLabel}>Role</div>
                <div className={styles.accessValue}>{form.role || data?.role}</div>
              </div>
            </div>
            <div className={styles.accessItem}>
              <div
                className={styles.accessIcon}
                style={{ background: "#f0fdf4", color: "#10b981" }}
              >
                <IconBriefcase size={20} />
              </div>
              <div>
                <div className={styles.accessLabel}>Position</div>
                <div className={styles.accessValue}>{form.position || data?.position}</div>
              </div>
            </div>
            <div className={styles.accessItem}>
              <div
                className={styles.accessIcon}
                style={{ background: "#fce8f3", color: "#e1306c" }}
              >
                <IconMapPin size={20} />
              </div>
              <div>
                <div className={styles.accessLabel}>Department</div>
                <div className={styles.accessValue}>{form.department || data?.department}</div>
              </div>
            </div>
          </div>
        ) : (
          <div className={styles.fieldGroup}>
            <Input
              label="Team"
              id="e-team"
              type="text"
              placeholder="e.g. Engineering Hiring"
              value={form.team}
              onChange={setField("team")}
            />
            <div className={styles.fieldRow}>
              <Select
                label="Role"
                id="e-role"
                value={form.role}
                onChange={setField("role")}
              >
                {ROLE_OPTIONS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </Select>
              <Input
                label="Position"
                id="e-position"
                type="text"
                value={form.position}
                onChange={setField("position")}
              />
            </div>
            <Input
              label="Department"
              id="e-department"
              type="text"
              value={form.department}
              onChange={setField("department")}
            />
            <div className={styles.editActions}>
              <Button
                variant="secondary"
                leftIcon={<IconCheck size={15} />}
                onClick={handleSave}
                loading={load}
                disabled={load}
              >
                Save Changes
              </Button>
              <Button
                variant="outlineBlue"
                onClick={cancelEdit}
                disabled={load}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
